//Built-in libraries
import { useState } from "react";

//Third party libraries
import {
  CalendarIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  IdentificationIcon,
  InformationCircleIcon,
  UserCircleIcon,
  UserGroupIcon,
} from "@heroicons/react/outline";
import { useMutation } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";

//Custom components
import { HookFormProvider } from "components/UI/HookForm/Provider";
import { HookForm } from "components/UI/HookForm/Form";
import Button from "components/UI/Button";
import SubmitSuccess from "components/Applications/SubmitSuccess";
import Attachments from "./Attachments";
import HouseholdIncome from "./HouseholdIncome";
import PropertyInformation from "./PropertyInformation";
import Review from "./Review";
import Application from "./ApplicationInformation";
import DefaultApplicantInformation from "../../Components/DefaultApplicantInformation";

//Services
import {
  ApplicationFormProvider,
  useApplicationFormContext,
} from "features/ApplicationForm/ApplicationFormProvider";
import { postApplication } from "api/Rover/ApplicationForm";
import {
  IGetApplicationFormInfoServiceRes,
  IPostApplicationServicePayload,
} from "api/Rover/ApplicationForm/types";

//STP Libraries

//Assets and Miscellaneous
import { initialValues } from "./constants";
import BDPALogo from "images/logos/bdpa.svg";
import {
  defaultStepsSchema,
  fakeSchema,
  step1Schema,
  step2Schema,
  step3Schema,
  step4Schema,
  userDocumentInfoSchema,
} from "./validationSchema";
import ApplicationPrerequisites from "./Prerequisities";

//Component Begin

const steps = [
  {
    name: "Prerequisites",
    sectionId: "userDocumentInfo",
    Icon: InformationCircleIcon,
    component: <ApplicationPrerequisites />,
    schema: userDocumentInfoSchema,
  },
  {
    name: "Applicant Information",
    sectionId: "applicantInformation",
    Icon: IdentificationIcon,
    component: <DefaultApplicantInformation />,
    schema: defaultStepsSchema,
  },
  {
    name: "Application",
    sectionId: "application",
    Icon: ClipboardListIcon,
    component: <Application />,
    schema: step1Schema,
  },
  {
    name: "Household Income",
    sectionId: "householdIncome",
    Icon: UserGroupIcon,
    component: <HouseholdIncome />,
    schema: step2Schema,
  },
  {
    name: "Property Information",
    sectionId: "propertyInformation",
    Icon: CalendarIcon,
    component: <PropertyInformation />,
    schema: step3Schema,
  },
  {
    name: "Attachments",
    sectionId: "attachments",
    Icon: UserCircleIcon,
    component: <Attachments />,
    schema: step4Schema,
  },
  {
    name: "Review",
    sectionId: "review",
    Icon: ClipboardCheckIcon,
    component: <Review />,
    schema: fakeSchema,
  },
];

interface IBDPAApplicationFormProps {
  formData: IGetApplicationFormInfoServiceRes;
}

const ApplicationFormBase = ({ formData }: IBDPAApplicationFormProps) => {
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const { currentStep, setCurrentStep, updateData, data } =
    useApplicationFormContext();

  const isLast = currentStep === steps.length - 1;

  const submitApplication = useMutation(
    (data: IPostApplicationServicePayload) => postApplication({ data }),
    {
      onSuccess: () => {
        setIsSubmitSuccess(true);
      },
    }
  );

  const onSubmitHandler = (values: Record<string, any>) => {
    if (
      currentStep === 0 &&
      values.userDocumentInfo.length === 1 &&
      (values.userDocumentInfo.includes("hasLetter") ||
        values.userDocumentInfo.includes("hasNothing"))
    ) {
      steps.splice(4, 1);
    }
    if (!isLast) {
      updateData({
        [steps[currentStep].sectionId]: values[steps[currentStep].sectionId],
      });
      setCurrentStep((prev) => prev + 1);
    } else {
      const files: any = [];

      Object.keys(data.attachments).forEach((key) => {
        data.attachments[key].selectedFiles.forEach((f: any) => {
          files.push(f);
        });
      });
      const applicationData: IPostApplicationServicePayload = {
        formId: formData?.data?.id,
        contactName: data.applicantInformation.fullName,
        contactEmail: data.applicantInformation.contactEmail,
        applicationData: data,
        files: [...(files || [])],
      };
      submitApplication.mutate(applicationData);
    }
  };

  if (isSubmitSuccess) {
    return (
      <SubmitSuccess
        message={
          "Thank you for your interest in Birmingham Down Payment Assistance."
        }
      />
    );
  }

  return (
    <div className="container mx-auto">
      <h2>{formData.data.name}</h2>
      <div className="flex mb-10">
        <aside className="w-1/4 py-6 px-2 sm:px-6 bg-white rounded-l-md">
          <div className="w-full flex justify-center">
            <img
              src={BDPALogo}
              alt="Form Logo"
              className="h-14 xl:h-20 object-scale-down "
            />
          </div>
          <nav className="space-y-1 py-5">
            {currentStep === 0 && (
              <button
                className={`${
                  currentStep === 0
                    ? "bg-neutralColor-lightest text-primaryColor-normal"
                    : "text-neutralColor-normal"
                } group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full`}
              >
                {steps[0].Icon}
                {steps[0].name}
              </button>
            )}
            {currentStep > 0 &&
              steps.map(({ name, Icon }, index) => (
                <button
                  key={index}
                  className={`${
                    currentStep === index
                      ? "bg-neutralColor-lightest text-primaryColor-normal"
                      : "text-neutralColor-normal"
                  } group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full`}
                >
                  <Icon className="flex-shrink-0 -ml-1 mr-3 h-6 w-6" />
                  {name}
                </button>
              ))}
          </nav>
        </aside>
        <div className="w-full bg-gray-50">
          <HookFormProvider
            defaultValues={initialValues}
            mode="all"
            resolver={yupResolver(steps[currentStep].schema)}
          >
            <HookForm onSubmit={onSubmitHandler}>
              {steps[currentStep].component}
              {isLast && (
                <div className="flex gap-4 justify-end p-6">
                  <Button
                    label="Previous"
                    btnStyle="neutral"
                    type="button"
                    disabled={submitApplication.isLoading}
                    onClick={() => setCurrentStep((prev) => prev - 1)}
                  />
                  <Button
                    label={`${
                      submitApplication.isLoading ? "Submitting..." : "Submit"
                    }`}
                    btnStyle="primary"
                    type="submit"
                    disabled={submitApplication.isLoading}
                  />
                </div>
              )}
            </HookForm>
          </HookFormProvider>
        </div>
      </div>
    </div>
  );
};

export const BDPAForm = ({ ...props }: IBDPAApplicationFormProps) => {
  return (
    <ApplicationFormProvider initialValues={initialValues}>
      <ApplicationFormBase {...props} />
    </ApplicationFormProvider>
  );
};
